import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useBlocker, useLocation } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, IconButton } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { Close } from "@mui/icons-material";
import { clearGasServedSelectedAreas, selectGasServedHasSelectedAreas, setGasServed } from "views/shared/organization-profile-stepper/slice";
import { showErrorToaster } from "store/Shared.Slice";

// Organization Profile only: keep the triggering action until the user decides
// what to do with the picker selections. Project and onboarding dialogs retain
// their existing confirmation behavior.
export default function useGeographicAreaConfirmation({ enabled, onSave }) {
    const { t } = useTranslation();
    const location = useLocation();
    const dispatch = useDispatch();
    const hasSelections = useSelector(selectGasServedHasSelectedAreas);
    const shouldConfirm = enabled && hasSelections;
    const addAreaRef = useRef(null);
    const pendingRef = useRef(null);
    const busyRef = useRef(false);
    const mountedRef = useRef(true);
    useEffect(() => {
        mountedRef.current = true;
        return () => { mountedRef.current = false; };
    }, []);
    const [pending, setPending] = useState(null);
    const [busy, setBusy] = useState(false);

    const save = useCallback(async (areas) => {
        try {
            const success = (await onSave?.(areas)) !== false;
            if (!success) dispatch(showErrorToaster(t("messages.geographic_areas_save_failed")));
            return success;
        } catch {
            dispatch(showErrorToaster(t("messages.geographic_areas_save_failed")));
            return false;
        }
    }, [onSave, dispatch, t]);

    const withConfirmation = useCallback((action, { saveBeforeContinue = false } = {}) => () => {
        if (busyRef.current || pendingRef.current) return;
        if (shouldConfirm) {
            const next = { action, saveBeforeContinue };
            pendingRef.current = next;
            setPending(next);
            return;
        }
        if (!saveBeforeContinue) return action?.();
        busyRef.current = true;
        setBusy(true);
        return save().then((success) => {
            if (success) action?.();
        }).finally(() => {
            busyRef.current = false;
            if (mountedRef.current) setBusy(false);
        });
    }, [shouldConfirm, save]);

    // Keep the blocker registration stable while a save rerenders its parent.
    // The router's retry must release the same registration that captured it.
    const requestRef = useRef(withConfirmation);
    useLayoutEffect(() => { requestRef.current = withConfirmation; }, [withConfirmation]);
    const blockNavigation = useCallback((transition) => {
        if (transition.location.pathname === location.pathname) {
            transition.retry();
            return;
        }
        requestRef.current(() => transition.retry())();
        // Re-arm after same-page navigation, since retry releases its blocker.
    }, [location]);
    useBlocker(blockNavigation, shouldConfirm || (enabled && busy));

    // Browser refresh/close cannot display an application dialog or await a save.
    // Do not listen to visibility/focus: switching browser tabs is harmless.
    useEffect(() => {
        if (!shouldConfirm && !(enabled && busy)) return;
        const warnBeforeUnload = (event) => {
            event.preventDefault();
            event.returnValue = "";
        };
        window.addEventListener("beforeunload", warnBeforeUnload);
        return () => window.removeEventListener("beforeunload", warnBeforeUnload);
    }, [shouldConfirm, enabled, busy]);

    const dismiss = () => {
        if (busyRef.current) return;
        pendingRef.current = null;
        setPending(null);
    };

    const continueAction = async (add) => {
        if (busyRef.current || !pendingRef.current) return;
        const next = pendingRef.current;
        busyRef.current = true;
        setBusy(true);
        try {
            if (add) {
                let areas;
                try {
                    if (!addAreaRef.current) throw new Error("Picker is unavailable");
                    areas = addAreaRef.current();
                } catch {
                    dispatch(showErrorToaster(t("messages.geographic_areas_add_failed")));
                    return;
                }
                // Persist the exact next list before clearing selections or leaving.
                if (!(await save(areas))) return;
                dispatch(setGasServed(areas));
            } else if (next.saveBeforeContinue && !(await save())) {
                return;
            }
            dispatch(clearGasServedSelectedAreas());
            pendingRef.current = null;
            setPending(null);
            next.action?.();
        } finally {
            busyRef.current = false;
            if (mountedRef.current) setBusy(false);
        }
    };

    const confirmationDialog = (
        <Dialog open={pending !== null} onClose={dismiss} fullWidth maxWidth="sm"
            aria-labelledby="profile-area-confirmation-title" aria-describedby="profile-area-confirmation-description">
            <DialogTitle id="profile-area-confirmation-title" sx={{ pr: 6 }}>
                {t("messages.geographic_areas_confirm_title")}
            </DialogTitle>
            <IconButton aria-label={t("buttons.close")} onClick={dismiss} disabled={busy}
                sx={{ position: "absolute", right: 8, top: 8 }}><Close /></IconButton>
            <DialogContent>
                <DialogContentText id="profile-area-confirmation-description">
                    {t("messages.geographic_areas_confirm_description")}
                </DialogContentText>
            </DialogContent>
            <DialogActions sx={{ p: 2, flexWrap: "wrap", gap: 1 }}>
                <Button onClick={() => continueAction(false)} disabled={busy} color="secondary">
                    {t("buttons.continue_without_adding")}
                </Button>
                <LoadingButton autoFocus onClick={() => continueAction(true)} loading={busy} variant="contained" color="secondary">
                    {t("buttons.add_and_continue")}
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );

    return { withConfirmation, confirmationDialog, addAreaRef, busy };
}
